import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { banUser, unbanUser } from '../api/authAdmin';
import { getSelectedEnvironment } from './apiEnvironment';
import {
  BAN_ACTION,
  UNBAN_ACTION,
  getBanAction,
  getUserId,
  getUserUsername,
  mapBanError,
} from './userBanActions';
import { t } from './i18n';

/**
 * Shared ban/unban mutations for User table and UserDetailModal.
 */
export function useUserBanActions({ token, apiEnvironment, role, cacheSession, onUnauthorized }) {
  const queryClient = useQueryClient();
  const [pendingUserId, setPendingUserId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const dialogScope = useRef(null);
  const scopeMatches = () => dialogScope.current?.environment === apiEnvironment && dialogScope.current?.token === token && dialogScope.current?.role === role && dialogScope.current?.cacheSession === cacheSession;

  useEffect(() => {
    dialogScope.current = null;
    setDialog(null);
  }, [apiEnvironment, token, role, cacheSession]);

  const invalidateAfterBanChange = (userId) => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    queryClient.invalidateQueries({ queryKey: ['usersSummary', apiEnvironment] });
    queryClient.invalidateQueries({ queryKey: ['userBanLogs'] });
    if (userId != null) {
      queryClient.invalidateQueries({ queryKey: ['userById', apiEnvironment, userId] });
      queryClient.invalidateQueries({ queryKey: ['user-detail', apiEnvironment] });
    }
  };

  const banMutation = useMutation({
    mutationFn: (userId) => banUser(userId, token),
    onSettled: () => setPendingUserId(null),
  });

  const unbanMutation = useMutation({
    mutationFn: (userId) => unbanUser(userId, token),
    onSettled: () => setPendingUserId(null),
  });

  const isPending = banMutation.isPending || unbanMutation.isPending;

  const closeDialog = () => {
    if (isPending) return;
    setDialog(null);
  };

  const runBanAction = (user) => {
    const action = getBanAction(user);
    const userId = getUserId(user);
    if (!action || userId == null || !token) return;

    const username = getUserUsername(user) || String(userId);
    const messageKey = action === BAN_ACTION ? 'user.ban.confirmBan' : 'user.ban.confirmUnban';
    dialogScope.current = { environment: apiEnvironment, token, role, cacheSession };
    setDialog({
      kind: 'confirm',
      user,
      action,
      message: t(messageKey, { id: userId, username }),
    });
  };

  const confirmDialog = async () => {
    if (!dialog || dialog.kind !== 'confirm' || !scopeMatches() || getSelectedEnvironment() !== apiEnvironment) return;

    const { user, action } = dialog;
    const userId = getUserId(user);
    if (userId == null || !token) return;
    const operationScope = dialogScope.current;

    setPendingUserId(userId);
    try {
      if (action === BAN_ACTION) {
        await banMutation.mutateAsync(userId);
      } else {
        await unbanMutation.mutateAsync(userId);
      }
      if (operationScope !== dialogScope.current || !scopeMatches()) return;
      invalidateAfterBanChange(userId);
      const successKey = action === BAN_ACTION ? 'user.ban.successBan' : 'user.ban.successUnban';
      setDialog({
        kind: 'alert',
        variant: 'success',
        message: t(successKey, { id: userId }),
      });
    } catch (err) {
      if (operationScope !== dialogScope.current || !scopeMatches()) return;
      if (err?.status === 401) {
        setDialog(null);
        onUnauthorized?.();
        return;
      }
      setDialog({
        kind: 'alert',
        variant: 'error',
        message: mapBanError(err),
      });
    }
  };

  return {
    runBanAction,
    isPending,
    pendingUserId,
    getBanAction,
    dialog: scopeMatches() ? dialog : null,
    confirmDialog,
    closeDialog,
  };
}

export { BAN_ACTION, UNBAN_ACTION, getBanAction };
